import React from 'react';
import { CheckCircle, XCircle, Clock, ExternalLink } from 'lucide-react';
import {
    TransactionStatus,
    TransactionType,
    formatSignature,
    getExplorerUrl,
} from '../utils/transactionSimulator';

/**
 * TransactionHistory Component
 *
 * Displays transaction history with:
 * - Status indicators
 * - Transaction details
 * - Links to explorer (simulated)
 * - Timestamps
 *
 * This component is standalone and can be easily modified.
 */
export default function TransactionHistory({ transactions, maxItems = 10 }) {
    if (!transactions || transactions.length === 0) {
        return (
            <div className="bg-black/40 backdrop-blur-sm border border-purple-500/30 rounded-2xl p-6">
                <h3 className="text-lg font-bold mb-4">Transaction History</h3>
                <div className="text-center text-gray-400 py-8">
                    No transactions yet
                </div>
            </div>
        );
    }

    const displayTransactions = transactions.slice(0, maxItems);

    return (
        <div className="bg-black/40 backdrop-blur-sm border border-purple-500/30 rounded-2xl p-6">
            <h3 className="text-lg font-bold mb-4">Transaction History</h3>
            <div className="space-y-3">
                {displayTransactions.map((tx, index) => (
                    <TransactionItem key={tx.signature || index} transaction={tx} />
                ))}
            </div>
        </div>
    );
}

function TransactionItem({ transaction }) {
    const {
        signature,
        type,
        amount,
        description,
        status,
        timestamp,
        error,
    } = transaction;

    // Status icon and color
    const getStatusDisplay = () => {
        switch (status) {
            case TransactionStatus.SUCCESS:
                return {
                    icon: <CheckCircle size={20} />,
                    color: 'text-green-400',
                    bgColor: 'bg-green-500/20',
                    text: 'Success',
                };
            case TransactionStatus.FAILED:
                return {
                    icon: <XCircle size={20} />,
                    color: 'text-red-400',
                    bgColor: 'bg-red-500/20',
                    text: 'Failed',
                };
            case TransactionStatus.CONFIRMING:
                return {
                    icon: <Clock size={20} className="animate-spin" />,
                    color: 'text-yellow-400',
                    bgColor: 'bg-yellow-500/20',
                    text: 'Confirming',
                };
            default:
                return {
                    icon: <Clock size={20} />,
                    color: 'text-gray-400',
                    bgColor: 'bg-gray-500/20',
                    text: 'Pending',
                };
        }
    };

    // Type display
    const getTypeDisplay = () => {
        switch (type) {
            case TransactionType.DEPLOY:
                return { text: 'Deploy', color: 'text-blue-400' };
            case TransactionType.CLAIM:
                return { text: 'Claim', color: 'text-orange-400' };
            case TransactionType.STAKE:
                return { text: 'Stake', color: 'text-purple-400' };
            case TransactionType.UNSTAKE:
                return { text: 'Unstake', color: 'text-pink-400' };
            default:
                return { text: 'Transaction', color: 'text-gray-400' };
        }
    };

    const statusDisplay = getStatusDisplay();
    const typeDisplay = getTypeDisplay();

    // Format timestamp
    const timeAgo = () => {
        const seconds = Math.floor((Date.now() - timestamp) / 1000);
        if (seconds < 60) return `${seconds}s ago`;
        const minutes = Math.floor(seconds / 60);
        if (minutes < 60) return `${minutes}m ago`;
        const hours = Math.floor(minutes / 60);
        if (hours < 24) return `${hours}h ago`;
        const days = Math.floor(hours / 24);
        return `${days}d ago`;
    };

    return (
        <div className="p-4 bg-gray-800/30 rounded-lg hover:bg-gray-800/50 transition-all">
            <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-3">
                    <div className={`${statusDisplay.bgColor} ${statusDisplay.color} p-2 rounded-lg`}>
                        {statusDisplay.icon}
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <span className={`text-sm font-semibold ${typeDisplay.color}`}>
                                {typeDisplay.text}
                            </span>
                            <span className="text-xs text-gray-500">{timeAgo()}</span>
                        </div>
                        <div className="text-sm text-gray-300">{description}</div>
                    </div>
                </div>
                <div className={`text-xs px-2 py-1 rounded ${statusDisplay.bgColor} ${statusDisplay.color}`}>
                    {statusDisplay.text}
                </div>
            </div>

            {signature && (
                <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-700">
                    <div className="text-xs text-gray-500 font-mono">
                        {formatSignature(signature)}
                    </div>
                    <a
                        href={getExplorerUrl(signature)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-purple-400 hover:text-purple-300 flex items-center gap-1"
                    >
                        View <ExternalLink size={12} />
                    </a>
                </div>
            )}

            {error && (
                <div className="mt-2 text-xs text-red-400 bg-red-500/10 px-2 py-1 rounded">
                    {error}
                </div>
            )}
        </div>
    );
}
